// MATERIAL UI
import { Box, FormControl, Grid } from "@mui/material";

// INTERFACES
import { Token } from "./CryptoWithdrawalModal";

// STYLES
import { StyledReviewDetails, StyledReviewTitle } from "../common/styles/CryptoWithdrawalStyled";

// ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface ICryptoWithdrawalCompletedProps {
  token: Token | null;
  withdrawalAddress: string | null;
  withdrawalAmount: number;
  memo?: string;
}

/**
 * The CryptoWithdrawalStageCompleted component.
 * This component is used to render the completed stage of the withdrawal process.
 *
 * @function
 *
 * @property {Token | null} - The given token, e.g. BTC
 * @property {string | null} withdrawalAddress - The selected address.
 * @property {number} withdrawalAmount - The withdrawal amount.
 * @property {string | null} memo - The memo.
 * @property {boolean} isCompleted - Whether the withdrawal is completed.
 *
 * @returns {JSX.Element} The JSX code to render the CryptoWithdrawalStageThree component.
 */
export const CryptoWithdrawalStageCompleted = ({
  token,
  withdrawalAddress,
  withdrawalAmount,
  memo,
}: ICryptoWithdrawalCompletedProps): JSX.Element => {
  return (
    <Grid container id="withdraw-modal-complete">
      <Grid
        item
        xs={12}
        px={2}
        py={1}
        style={{
          background: "#F0FEF9",
          borderRadius: "8px",
          fontSize: "14px",
          display: "flex",
        }}>
        <Grid>
          <CheckCircleIcon sx={{ color: "#000000", width: "16px", marginRight: "8px" }} />
        </Grid>
        <Grid>
          {`We received your request to withdraw ${withdrawalAmount} ${token?.code}. You’ll be notified via email when this
      has been completed.`}
        </Grid>
      </Grid>
      <Grid xs={12} py={1.75} item>
        <FormControl fullWidth>
          <StyledReviewTitle>Token:</StyledReviewTitle>
          {token && (
            <Box display="inline-flex" alignItems="center">
              <StyledReviewDetails>
                <img
                  src={token.logoUrl || ""}
                  width={16}
                  alt={token.code}
                  style={{ verticalAlign: "middle", marginRight: "8px" }}
                />
                {token.displayName}
                {` (${token.code})`}
              </StyledReviewDetails>
            </Box>
          )}
        </FormControl>
      </Grid>
      <Grid xs={12} py={1.75} item>
        <FormControl fullWidth>
          <StyledReviewTitle>Address:</StyledReviewTitle>
          <StyledReviewDetails>{withdrawalAddress}</StyledReviewDetails>
        </FormControl>
      </Grid>
      <Grid xs={12} py={1.75} item>
        <FormControl fullWidth>
          <StyledReviewTitle>Format/Network:</StyledReviewTitle>
          <StyledReviewDetails>{token?.format || "Unknown"}</StyledReviewDetails>
        </FormControl>
      </Grid>
      <Grid xs={12} py={1.75} item>
        <FormControl fullWidth>
          <StyledReviewTitle>Amount Withdrawn:</StyledReviewTitle>
          <StyledReviewDetails>{withdrawalAmount}</StyledReviewDetails>
        </FormControl>
      </Grid>
      {token?.memo && (
        <Grid xs={12} py={1.75} item>
          <FormControl fullWidth>
            <StyledReviewTitle>Memo:</StyledReviewTitle>
            <StyledReviewDetails>{memo}</StyledReviewDetails>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};
